import {writable, get} from 'svelte/store';
import {currentUser, mainAPI, notificationUtils} from 'root/angular-injector-provider';
import {generateId} from 'utils/id';

const MINIMUM_OBJECT_ID = generateObjectIdFromAYearAgo();

const INITIAL_STATE = {
  _storage: {
    id: '',
    timeId: '',
    settingsId: '',
  },
  data: {},
  settings: {showBidStates: true},
  unread: false,
}

const store = writable(INITIAL_STATE);

const bChannelId = generateId();
const bChannel = new BroadcastChannel('user-messaging');

export default store;

let activeRequestId = null;
let currentUserAccountId = null;
let oldestUnreadMessage = null;

(function loop() {
  setTimeout(async () => {
    await refreshReadMessages();
    loop();
  }, 60000);
})();

bChannel.onmessage = e => {
  if(e.data.channelId !== bChannelId && e.data.userAccountId === currentUserAccountId) {
    store.update($s => ({...$s, ...e.data.update}))
  }
}


export function initialize() {
  store.set(INITIAL_STATE);
  currentUser().watch(initializeStore);
}

async function initializeStore(){
  store.set(INITIAL_STATE);
  activeRequestId = null;
  currentUserAccountId = null;
  oldestUnreadMessage = null;

  try {
    const _storage = await initializeStorage();
    if (_storage.id) {
      const data = JSON.parse(localStorage.getItem(_storage.id) || '{}');
      const lastUpdated = localStorage.getItem(_storage.timeId) || null;
      const settings = JSON.parse(localStorage.getItem(_storage.settingsId) || '{"showBidStates": true}');
      store.set({_storage, data, lastUpdated, settings, unread: false});
      currentUserAccountId = _storage.currentUserAccountId;
      oldestUnreadMessage = MINIMUM_OBJECT_ID > currentUserAccountId ? MINIMUM_OBJECT_ID : currentUserAccountId;
      await refreshReadMessages();
    }
  } catch (ignoredError) {
    // ignore
  }
}

export async function updateReadMessages(){
  await refreshReadMessages();
}

async function refreshReadMessages(){
  if(activeRequestId || !currentUserAccountId) return;
  const thisRequestId = new Date().getTime();
  activeRequestId = thisRequestId;

  try {
    const $store = get(store);
    const httpResponse = await mainAPI().messaging.getCurrentUserMessagesStatus($store.lastUpdated || '');
    const rData = httpResponse.data;
    if (rData && rData.userAccountId === currentUserAccountId && thisRequestId === activeRequestId) {
      store.update($s => {
        const update = createStoreUpdate($s, rData);
        const lastUpdated = rData.updated || $s.lastUpdated || null;
        const data = {...$s.data, ...(rData.data || {})}
        if(update.lastUpdated) localStorage.setItem($s._storage.timeId, lastUpdated);
        if(update.data) localStorage.setItem($s._storage.id, JSON.stringify(data));
        if(bChannel) bChannel.postMessage({channelId: bChannelId, update, userAccountId: currentUserAccountId});

        return {...$s, ...update};
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }

  if(thisRequestId === activeRequestId) {activeRequestId = null;}
}

function createStoreUpdate(s, updateData){
  const lastUpdated = updateData.updated || s.lastUpdated || null;
  const data = {...s.data, ...(updateData.data || {})}
  return {data, lastUpdated, unread: updateData.unread || false};
}

export function isUnread(subject, lastMessageId) {
  return isUnreadMessage(subject, lastMessageId, get(store).data);
}

export function isUnreadMessage(subject, lastMessageId, storeData) {
  if(lastMessageId < oldestUnreadMessage) return false;
  const lastReadMessageId = storeData[subject];
  return !lastReadMessageId || lastReadMessageId < lastMessageId;
}

export async function markUserMessagesAsRead(){
  await notificationUtils().onLoadWithNotification(async function(){
    await mainAPI().messaging.markUserMessagesAsRead();
    await updateReadMessages();
  });
}

export async function markAccountMessagesAsRead(){
  await notificationUtils().onLoadWithNotification(async function(){
    await mainAPI().messaging.markAccountMessagesAsRead();
    await updateReadMessages();
  });
}

export function updateSettings(key, value){
  store.update($s => {
    const settings = {...$s.settings, [key]: value};
    localStorage.setItem($s._storage.settingsId, JSON.stringify(settings));
    return {...$s, settings};
  });
}


async function initializeStorage(){
  const cu = await currentUser().getActiveUserAccount();
  const id = `RB-MSG-${cu.id}`;
  const timeId = `${id}_TIME`;
  const settingsId = `${id}_SETTINGS`;

  return { id, timeId, settingsId, currentUserAccountId: cu.id };
}


function generateObjectIdFromAYearAgo(){
  const t = parseInt((new Date().getTime()/1000) - 31622400, 10) // current seconds - seconds in a year;
  return `${hex(8,t % 0xFFFFFFFF)}0000000000000000`;

  function hex(length, n) {
    n = n.toString(16);
    return (n.length===length)? n : '00000000'.substring(n.length, length) + n;
  }
}
